import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  Checkbox,
  Icon,
  List,
  Modal,
  ModalFooter,
  MultiColumnList,
} from '@folio/stripes-components';
import { Pluggable } from '@folio/stripes/core';
import { getFullName, getLocaleLabel, ImportLocalesIcones } from '../../utils/utils';

function AssignTranslatorModal({ open, onClose, onSave, languagesList }) {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedLocales, setSelectedLocales] = useState({});
  const intl = useIntl();
  const flags = ImportLocalesIcones();

  const handleSelectUsers = (users) => {
    setSelectedUsers(users);
  };

  const toggleLocale = (localeValue) => {
    setSelectedLocales((prev) => ({
      ...prev,
      [localeValue]: !prev[localeValue],
    }));
  };

  const toggleAll = () => {
    const allSelected = languagesList.every((l) => selectedLocales[l.localeValue]);
    const next = {};
    languagesList.forEach((l) => { next[l.localeValue] = !allSelected; });
    setSelectedLocales(next);
  };

  const handleSave = () => {
    const localeValues = languagesList
      .filter((l) => selectedLocales[l.localeValue])
      .map((l) => l.localeValue);
    onSave(selectedUsers, localeValues);
    handleClose();
  };

  const handleClose = () => {
    setSelectedUsers([]);
    setSelectedLocales({});
    onClose();
  };

  const removeUser = (userId) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const allSelected = languagesList.length > 0
    && languagesList.every((l) => selectedLocales[l.localeValue]);
  const anyLocaleSelected = Object.values(selectedLocales).some(Boolean);
  const canSave = selectedUsers.length > 0 && anyLocaleSelected;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      dismissible
      size="medium"
      label={
        <Icon icon="plus-sign">
          <FormattedMessage
            id="ui-translations.settings.languagesTranslators.assignModal.title"
            defaultMessage="Assign Translators to Multiple Languages"
          />
        </Icon>
      }
      footer={
        <ModalFooter>
          <Button
            buttonStyle="primary"
            disabled={!canSave}
            onClick={handleSave}
          >
            <FormattedMessage id="stripes-core.button.save" />
          </Button>
          <Button onClick={handleClose}>
            <FormattedMessage id="stripes-components.cancel" />
          </Button>
        </ModalFooter>
      }
    >
      {/* Step 1: Select Translators */}
      <div style={{ marginBottom: '1rem' }}>
        <strong>
          <FormattedMessage
            id="ui-translations.settings.languagesTranslators.assignModal.selectTranslator"
            defaultMessage="1. Select Translators"
          />
        </strong>
        <div style={{ marginTop: '0.5rem' }}>
          <Pluggable
            aria-haspopup="true"
            type="find-user"
            id="clickable-find-translators-assign"
            dataKey="find-translators-assign"
            searchButtonStyle="default"
            searchLabel={
              <>
                <Icon icon="plus-sign" />
                {' '}
                <FormattedMessage
                  id="ui-translations.settings.languagesTranslators.assignModal.chooseTranslator"
                  defaultMessage="Choose Translators"
                />
              </>
            }
            selectUsers={handleSelectUsers}
            visibleColumns={['status', 'name', 'patronGroup', 'username', 'barcode']}
            disableRecordCreation
          >
            <Button buttonStyle="default">
              <Icon icon="plus-sign" />
              {' '}
              <FormattedMessage
                id="ui-translations.settings.languagesTranslators.assignModal.chooseTranslator"
                defaultMessage="Choose Translators"
              />
            </Button>
          </Pluggable>
        </div>

        {selectedUsers.length > 0 && (
          <div style={{ marginTop: '0.5rem' }}>
            <List
              listStyle="bullets"
              items={selectedUsers}
              itemFormatter={(user) => (
                <li key={user.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Icon icon="profile">
                      {getFullName(user) || user.username}
                    </Icon>
                    <Button
                      buttonStyle="none"
                      marginBottom0
                      onClick={() => removeUser(user.id)}
                    >
                      <Icon icon="times-circle" size="small" />
                    </Button>
                  </div>
                </li>
              )}
            />
          </div>
        )}
      </div>

      {/* Step 2: Select Languages */}
      <div>
        <strong>
          <FormattedMessage
            id="ui-translations.settings.languagesTranslators.assignModal.selectLanguages"
            defaultMessage="2. Select Languages"
          />
        </strong>
        <MultiColumnList
          interactive={false}
          contentData={languagesList}
          visibleColumns={['selected', 'name']}
          columnWidths={{ selected: '10%', name: '90%' }}
          columnMapping={{
            selected: (
              <Checkbox
                checked={allSelected}
                onChange={toggleAll}
                onMouseDown={(e) => e.preventDefault()}
              />
            ),
            name: intl.formatMessage({
              id: 'ui-translations.settings.languages.columns.name',
            }),
          }}
          formatter={{
            selected: (lang) => (
              <Checkbox
                checked={!!selectedLocales[lang.localeValue]}
                onChange={() => toggleLocale(lang.localeValue)}
              />
            ),
            name: (lang) => (
              <Icon icon={flags[lang.localeValue] || 'flag'}>
                {getLocaleLabel(lang.localeValue, intl)}
              </Icon>
            ),
          }}
          isSelected={({ item }) => !!selectedLocales[item.localeValue]}
        />
      </div>
    </Modal>
  );
}

AssignTranslatorModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  languagesList: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default AssignTranslatorModal;
